import { AfterViewInit, Component, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';

import { Observable, Subject, combineLatest, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';

import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { AccesService } from '../services/acces.service';
import { AccesTLR } from '../entities/acces-tlr.entity';

import { AntenneService } from '../../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';

import { ParcService } from '../../../structure/parc/services/parc.service';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity'; 

import { UserService } from '../../../humain/user/services/user.service';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { MultiSelection } from '../../../../shared/checkbox-list/entities/multi-selection.entity';

class Row {
	acces!: AccesTLR;
	antenne!: AntenneTLR;
	nomsOuvrages!: string;
	parc!: ParcTLR;
	parcIcon!: string;
	cac!: UserTLR | null;
	entreprise!: string;
	ca!: string;
	refsCarl: string[] = [];
}

@Component({
	selector: 'acces-widget',
	templateUrl: 'acces-widget.html',
	styleUrls: ['acces-widget.css'],
})
export class AccesWidgetComponent implements AfterViewInit {
	// Elements de fonctionnement du tableau
	displayedColumns: string[] = ['numero', 'antenne', 'parc', 'ouvrages', 'cac', 'entreprise', 'ca', 'debut', 'autorisation', 'controleSecurite', 'numCrCarl'];
	dataSource: MatTableDataSource<Row> = new MatTableDataSource([] as Row[]);
	
	// Libelle pour les controles de securite
	controlesSecurite = new Map<string, string>();
	
	// Informations a bind sur la vue
	isUserSuperviseur!: boolean;
	antennes: AntenneTLR[] = []; 
	antennesSelected = new Map<number, boolean>();
	
	// Message pour le filtrage par antenne
	private antenneIdsSubject = new Subject<MultiSelection>();

	constructor(
		@Inject(AccesService) accesService: AccesService,
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(UserService) userService: UserService,
	) {
		this.controlesSecurite.set('HABILITATION_KO', 'Habilitations KO, Accord Exploitation');
		this.controlesSecurite.set('PDP_KO', 'Plan de prévention KO, Accord Exploitation');
		this.controlesSecurite.set('COACTIVITE_KO', 'Coactivité KO, Accord Exploitation');
		this.controlesSecurite.set('PLANNING_KO', 'Planning KO, Accord Exploitation');
		this.controlesSecurite.set('INTERVENANT_INCONNU', 'Intervenant inconnu');
		
		// Obtenir l'uitilisateur et en deriver les consequence pour la vue
		let user$ = userService.getCurrentUserTLR();
		user$.subscribe(
			(user) => {
				this.isUserSuperviseur = (user.role == 'CCE');
				this.displayedColumns = this.isUserSuperviseur ? //
					['numero', 'antenne', 'parc', 'ouvrages', 'cac', 'debut', 'autorisation', 'controleSecurite', 'numCrCarl'] //
					: //
					['numero', 'parc', 'ouvrages', 'cac', 'entreprise', 'ca', 'debut', 'autorisation', 'controleSecurite', 'numCrCarl'] //
				;
				
				if (!this.isUserSuperviseur) {
					// Pour les non-superviseur, pas de filtrage
					this.antenneIdsSubject.next(new MultiSelection([], true));
				}
			}
		);
		
		// Obtenir la liste complete de utilisateurs connus
		let usersById$ = userService.getUserTLRMapById();
		
		// Definir l'id de l'antenne comme un sous produit de l'utilisateur
		let idAntenne$: Observable<number | null> = user$.pipe(
			mergeMap(
				(user) => {
					if (user.role === 'CCE')
						return of(null);
						
					if (user.antenneId)
						return of(user.antenneId);
					
					// Cas special pour les directeurs d'agence: on prend la premiere antenne de leur agence
					return antenneService.getAntenneTLRByAgence(user.agenceId).pipe(
						map((antennes) => antennes[0].id)
					); 
				}
			)
		);
		
		// Charger la liste complete des antennes connues
		let antennes$ = antenneService.getAntenneTLRList();
		antennes$.subscribe(
			(antennes) => {
				this.antennes = antennes;
				for (let antenne of antennes) {
					this.antennesSelected.set(antenne.id, true);
				}
			}
		);
		
		// Charger la liste complete des parcs connus
		let parcsById$ = parcService.getParcTLRMapById();
		
		// Charger la liste complete de entreprises connues
		let entreprisesById$ = entrepriseService.getActiveEntreprisesTLRMapById();
		
		// Charger la liste des acces en cours en fonction de l'id d'antenne concerne
		let accesList$ = idAntenne$.pipe(
			mergeMap(
				(idAntenne: number | null) => idAntenne ? accesService.getEnCoursByAntenneForWidget(idAntenne): accesService.getEnCoursForWidget()
			)
		);
		
		// Enrichir la liste des acces en cours avec pour chacun, son parc
		let accesListWithParcs$ = combineLatest([accesList$, parcsById$]).pipe(
			map(
				(data) => {
					let accesList = data[0];
					let parcsById = new MandatoryMap(data[1], "Parc");
					
					return accesList.map(
						(dto) => ({
							dto: dto,
							parc: parcsById.get(dto.acces.parcId)
						})
					);
				}
			)
		);
		
		// Definir la fonction de filtrage des acces a afficher en fonction de la selection faite sur les antennes
		let filterFunction$ = this.antenneIdsSubject.pipe(
			map(
				(selection: MultiSelection) => {
						if (selection.all) {
							return (_parc: ParcTLR) => true;
						}
					
						let antenneIds = selection.ids;
						return (parc: ParcTLR) => antenneIds.findIndex($ => $ === parc.antenneId) > -1;			
				}
			)
		);
		
		// Definir la liste des acces en cours a afficher en fonction de la liste complete des acces en cours et de la dite fonction de filtrage
		let filteredAccesListWithParcs$ = combineLatest([accesListWithParcs$, filterFunction$]).pipe(
			map(
				(data) => {
					let accesListWithParcs = data[0];
					let filterFunction = data[1];

					return accesListWithParcs.filter(
						$ => filterFunction($.parc) 
					);				
				}
			)
		);
		
		// Definir les lignes de tableau a afficher en fonction des acces a afficher et des elements complementaire pour alimenter les colonnes 
		let rows$ = combineLatest([filteredAccesListWithParcs$, usersById$, antennes$, entreprisesById$, this.refreshCommandsSubject]).pipe(
			map(
				(data) => {
					let accesListWithParcs = data[0];
					let usersById = new MandatoryMap(data[1], "User");
					let antennes = data[2];
					let entreprisesById = new MandatoryMap(data[3], "Entreprise");
					
					let antennesByIdTmp = new Map<number, AntenneTLR>();
					for (let antenne of antennes) {
						antennesByIdTmp.set(antenne.id, antenne);
					}
					let antennesById = new MandatoryMap(antennesByIdTmp, "Antenne");
					
					let rows = accesListWithParcs.map(
						(elem) => {
							let accesDto = elem.dto;
							let acces = accesDto.acces;
							let parc = elem.parc;
							let antenne = antennesById.get(parc.antenneId);
							let cac = acces.cacId == null ? null : usersById.get(acces.cacId);
							
							let onlyUnique = function (value: number, index: number, array: number[]) {
							  return array.indexOf(value) === index;
							}
							let entreprise = accesDto.intervenantsInfos //
								.map($ => $.entrepriseId) //
								.filter(onlyUnique) //
								.map($ => entreprisesById.get($)) //
								.map($ => $.nom) //
								.join(", ") //
							;
							let ca = accesDto.intervenantsInfos.map($ => $.nom).join(", ");

							let row: Row = {
								acces: acces,
								antenne: antenne,
								nomsOuvrages: accesDto.nomsOuvrages,
								parc: parc,
								parcIcon: parcService.getEnergieAsIcon(parc),
								cac: cac,
								entreprise: entreprise,
								ca: ca,
								refsCarl: accesDto.refsCarl,
							}
							return row;										
						}
					)

					return rows;
				}
			),
		);
		
		// Une fois les lignes determinees, les lier au data source, pour affichage
		rows$.subscribe(
			(rows) => {
				// Mise a jour du dataSource
				this.dataSource.data = rows;
			}
		);
	}

	@ViewChild(MatPaginator) paginator!: MatPaginator;
	
	// Liaison de l'observable servant a commander le rafraichissement des donnees
	private refreshCommandsSubject = new SubjectRelay<void>();
	@Input() set refreshCommands(obs: Observable<void> | undefined) {
		this.refreshCommandsSubject.registerSource(obs);
	}
	
	@Output() accesClicked = new EventEmitter<AccesTLR>();

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.refreshCommandsSubject.next();
	}
	
	onSelectedAntenneIdsChange(value: MultiSelection | undefined) {
		if (value) {
			this.antenneIdsSubject.next(value);
		}
	}
	
	onRowClicked(row: Row) {
		this.accesClicked.emit(row.acces);
	}
	
	gestionAccesUrl() {
		return window.location.href.substring(0, window.location.href.indexOf('#') + 1) + '/acces';
	}
}